// For some reason most of the enums in this file have a 'no-shadow' lint error.
/* eslint-disable no-shadow */
export enum SortDirection {
	Default = '',
	Ascending = 'asc',
	Descending = 'desc'
}

export enum DateFilter {
	RecentActivity = 'lastEventDate',
	RequestedDate = 'createdDate',
	BillingDate = 'billingTime',
	CompletionDate = 'checkCompletedDate',
	RenewalDate = 'renewalDate'
}

export const DateFilterOptions = [
	{ label: 'Recent Activity', value: DateFilter.RecentActivity },
	{ label: 'Requested Date', value: DateFilter.RequestedDate },
	{ label: 'Billing Date', value: DateFilter.BillingDate },
	{ label: 'Completion Date', value: DateFilter.CompletionDate },
	{ label: 'Renewal Date', value: DateFilter.RenewalDate }
];

export const toggleSortDirection = (value: SortDirection): SortDirection => {
	switch (value) {
		case SortDirection.Default:
		case SortDirection.Ascending:
			return SortDirection.Descending;
		case SortDirection.Descending:
			return SortDirection.Default;
		default:
			throw new Error(`Unexpected sortDirection: ${value}.`);
	}
};

export enum Role {
	CustomerAdmin = 3,
	ServiceAdmin = 5,
	CustomerUser = 6,
	SystemAdmin = 7,
	CustomerSuccess = 10,
	ReadOnly = 11
}

export enum Status {
	NotStarted = 'Not Started',
	Requested = 'Requested',
	Received = 'Received',
	Waiting = 'Waiting',
	InProgress = 'In progress',
	Cancelled = 'Cancelled',
	Completed = 'Completed',
	SystemError = 'System error',
	AttentionRequired = 'Attention required',
	Expired = 'Expired'
}

export enum WorkflowStatusForEventLog {
	ClearResult = 'Clear result',
	ReviewResult = 'Review result',
	ResultMarkedAsClear = 'Result marked as clear',
	ResultMarkedAsInconclusive = 'Result marked as inconclusive',
	ResultMarkedAsFlagged = 'Result marked as flagged',
	ClearResultConfirmed = 'Clear result confirmed',
	Requested = 'Requested',
	Received = 'Received',
	Waiting = 'Waiting',
	InProgress = 'In progress',
	Cancelled = 'Cancelled',
	SystemError = 'System error',
	AttentionRequired = 'Attention required',
	Expired = 'Expired'
}

const haltingStatuses = [Status.SystemError, Status.Cancelled];
export const finishedStatuses = [Status.Expired, Status.Completed].concat(haltingStatuses);
export const pendingStatuses = [
	Status.NotStarted,
	Status.Waiting,
	Status.InProgress,
	Status.Requested,
	Status.Received
];

export const candidateActionStatuses = [Status.NotStarted, Status.InProgress, Status.Requested, Status.Received];

export enum TimePeriod {
	AllTime = 'All Time',
	LastTwentyFourHours = 'Last 24 Hours',
	LastSevenDays = 'Last 7 Days',
	LastFourteenDays = 'Last 14 Days',
	LastThirtyDays = 'Last 30 Days',
	LastSixtyDays = 'Last 60 Days'
}

export enum Outcome {
	ClearResult = 'Clear result',
	ReviewResult = 'Review result',
	ResultMarkedAsClear = 'Result marked as clear',
	ResultMarkedAsInconclusive = 'Result marked as inconclusive',
	ResultMarkedAsFlagged = 'Result marked as flagged',
	ClearResultConfirmed = 'Clear result confirmed'
}

export enum CheckTypeAvailability {
	NotForCustomer = -1,
	AvailableForCustomer = 0,
	EnabledForCustomer = 1
}

export enum CheckType {
	Aref = 'AREF',
	Eref = 'EREF',
	Edref = 'EDREF',
	Mref = 'MREF',
	Pref = 'PREF',
	Seref = 'SEREF'
}

// The following rules are based on "NSS Web Services Interfaces (Agencies) v2.0.pdf"
export enum InputMaxLengthForAcic {
	LastName = 100, // Same for SingleName
	FirstName = 100,
	MiddleNames = 100,
	UnitNumber = 50,
	StreetNumber = 10,
	StreetName = 230, // UnitNumber + StreetNumber + StreetName < 255
	Suburb = 255,
	State = 50,
	Postcode = 15,
	BirthPlace = 100, // Town
	PassportNumber = 50,
	AuDriverLicenceNumber = 10,
	DefaultIdNumber = 100
}

// The following rules are based on "Basic DBS field Validation.xlsx in PHOENIX-890"
export enum InputMaxLengthForDbs {
	LastName = 50,
	FirstName = 60,
	MiddleNames = 60,
	UnitNumber = 50,
	StreetNumber = 10,
	StreetName = 40, // UnitNumber + StreetNumber + StreetName < 60 (AddressLine1)
	Suburb = 30,
	State = 30,
	Postcode = 30,
	BirthPlace = 30, // Town
	PassportNumber = 11,
	DriverLicenceNumber = 18,
	PositionAppliedFor = 60,
	EmployerName = 60,
	OtherPurpose = 100
}

// Following DBS/DS IdTypes must match the value in FileUploadImageType
export enum DbsIdTypes {
	Passport = 'Passport',
	DriverLicence = 'Driver Licence'
}

export enum DbsSecondaryIdTypes {
	UtilityBill = 'Utility Bill',
	BankStatement = 'Bank Statement',
	CreditCardStatement = 'Credit Card Statement',
	MortgageOrRentAgreement = 'Mortgage/Rent Agreement',
	DriverLicence = 'Driver Licence'
}

// DBS Group 1
export enum DbsPrimaryIdentityDocuments {
	Passport = 'Passport (current and valid)',
	UkResidencePermit = 'UK Biometric residence permit',
	UkPhotoDrivingLicense = 'UK Photocard Driving License (current and valid)',
	UkBirthCertificateWithinAYear = 'UK Birth certificate (issued within 12 months of birth)',
	AdoptionCertificate = 'Adoption certificate',
	DVAUKDriversLicense = 'UK Drivers License',
	NonUkDrivingLicence = 'Non-UK driving licence photocard',
	UkDrivingLicencePaperVersion = 'UK driving licence - paper version',
	EVisa = "E-Visa (PDF from 'view & prove' service)",
	Arc = 'ARC (Application registration card) - issued by the Home Office'
}

// DBS Group 2a
export enum DbsTrustedGovernmentDocuments {
	UkPhotoDrivingLicense = 'UK Photocard Driving License (current and valid)',
	UkPaperDrivingLicense = 'UK Paper Driving License (current and valid)',
	UkBirthCertificateAnytime = 'UK Birth certificate (issued any time after birth)',
	UkMarriageCertificate = 'UK Marriage/civil partnership certificate',
	UkHmForcesIdCard = 'UK HM Forces ID card',
	UkFirearmsLicence = 'UK Firearms licence'
}

// DBS Group 2b
export enum DbsFinancialAndSocialHistoryDocuments {
	UkMortgageStatement = 'UK Mortgage statement (issued in last 12 months)',
	UkBankStatement = 'UK Bank or building society statement (issued in last 3 months)',
	UkBankAccountOpeningLetter = 'UK Bank or building society account opening confirmation letter (issued in last 3 months)',
	UkCreditCardStatement = 'UK Credit card statement (issued in last 3 months)',
	UkFinancialStatement = 'UK Financial statement, for example pension or endowment (issued in last 12 months)',
	UkP45Statement = 'UK P45 or P60 statement (issued in last 12 months)',
	UkCouncilTaxStatement = 'UK Council Tax statement (issued in last 12 months)',
	UkUtilityBill = 'UK Utility bill (issued in last 3 months)',
	UkBenefitStatement = 'UK Benefit statement, for example Child Benefit, pension (issued in last 3 months)',
	UkGovernmentGivingEntitlement = 'UK Central or local government, government agency, or local council document giving entitlement, for example DWP, HMRC(issued in last 3 months)',
	EeaNationalIdCard = 'EEA National ID card (valid)',
	IrishPassportCard = 'Irish Passport Card (valid)',
	UkCardsWithAccreditationLogo = 'UK Cards carrying the PASS accreditation logo (valid)',
	EhicGhic = 'European Health Insurance Card (EHIC) or Global Health Insurance Card (GHIC)',
	HmrcSelfAssessment = 'HMRC self-assessment letters or tax demand letter (issued in the last 12 months)'
}

export enum DsIdTypes {
	Passport = 'Passport',
	DrivingLicenceWithPhoto = 'Driving Licence With Photo',
	DrivingLicenceWithoutPhoto = 'Driving Licence Without Photo',
	BirthCertificate = 'Birth Certificate',
	NationalIdCard = 'National ID Card',
	NationalEntitlementCard = 'National Entitlement Card'
}

export enum NameChangeDocumentTypes {
	MarriageCertificate = 'Marriage certificate',
	NameChangeDocument = 'Name change document'
}

export enum CommencementDocuments {
	ImmiCard = 'ImmiCard',
	EvidenceOfResidentStatus = 'Evidence of resident status',
	CertificateOfIdentity = 'Certificate of identity',
	DocumentOfIdentity = 'Document of identity'
}

export enum PrimaryDocuments {
	ForeignPassport = 'Passport issued by a country other than Australia',
	ProofOfAgeOrPhotoIdentityCard = 'Proof of age / photo identity card',
	ShootersOrFirearmsLicence = 'Shooters / firearms licence',
	StudentIdCardUnder18sOnly = 'Student ID card (under 18s only)'
}

export enum NZPrimaryDocuments {
	FirearmsLicence = 'NZ Firearms Licence',
	RefugeeTravelDocument = 'NZ Refugee Travel Document',
	EmergencyTravelDocuments = 'NZ Emergency Travel Documents',
	CertificateofIdentity = 'NZ Certificate of Identity'
}

export enum NZSecondaryDocuments {
	EighteenPlusCard = '18+ Card',
	FullBirthCertificate = 'NZ Full Birth Certificate (issued before 1998)',
	CommunityServicesCard = 'Community Services Card',
	SuperGoldCard = 'SuperGold Card',
	EmployeePhotoIDCard = 'NZ Employee Photo ID Card',
	StudentPhotoIDCard = 'NZ Student Photo ID Card',
	InlandRevenueNumber = 'Inland Revenue number',
	NZIssuedUtilityBill = 'NZ issued utility bill (issued within 6 months)',
	TeachersRegistrationCertificate = 'NZ Teachers Registration Certificate',
	ElectoralRollRecord = 'NZ Electoral Roll Record',
	InternationalDrivingPermit = 'International Driving Permit',
	StepsToFreedomForm = 'Steps to Freedom Form'
}

export enum POADocuments {
	UKUtilitybill = 'UK Utility bill',
	UKCouncilTaxBillletter = 'UK council tax bill/letter',
	UKBankorCreditCardStatement = 'UK Bank or credit card statement',
	UKMortgageStatement = 'UK Mortgage statement',
	UKTenancySgreement = 'UK Tenancy agreement',
	UKInsurancePolicyLetter = 'UK Insurance policy/letter',
	UKPayslip = 'UK Payslip/P60',
	UKHospitalLetter = 'UK Hospital/doctors letter',
	DL = 'DL',
	BiometricResidencePermit = 'Front and back of Biometric Residence Permit',
	EEANationalIDCard = 'EEA National ID card',
	UKArmedForces = 'UK Armed forces ID Card',
	UKFirearms = 'UK firearms/shot gun licence',
	UKNHSCard = 'UK NHS card'
}

export enum IDDocuments {
	Passport = 'Passport',
	DL = 'DL',
	BirthCertificate = 'Birth certificate',
	AdoptionCertificate = 'Adoption certificate',
	MarriageCertificate = 'Marriage/civil partnership certificate'
}

export enum GroupOneDocument {
	Passport = 'Passport (must be in date)',
	BiometricResidencePermit = 'Biometric Residence permit (both sides)',
	UkPhotoDrivingLicense = 'Current driving licence photocard (full or provisional) - UK, Isle of Man, Channel Islands, Ireland or EEA countries',
	UkBirthCertificateWithinAYear = 'Birth certificate issued at time of birth (UK, Isle of Man and Channel Islands: including those issued by UK authorities overseas, for example embassies, High Commissions and HM Forces)',
	LongFormIrishBirthCertificate = 'Long form Irish birth certificate issued at time of registration of birth',
	AdoptionCertificate = 'Adoption certificate (UK, Isle of Man or Channel Islands)',
	NoGroupOneDocument = 'Do not have Group 1 document (please upload 2 from Group 2a and 2 from Group 2b)'
}

export enum GroupTwoADocumentOne {
	BirthCertificateAfterBirth = 'Birth certificate issued after time of birth (UK, Isle of Man, Channel Islands or Ireland)',
	MarriageCivilPartnershipCertificate = 'Marriage / Civil Partnership certificate (UK, Isle of Man, Channel Islands or Ireland)',
	HMForcesIDCard = 'HM Forces ID card (UK)',
	FirearmsLicence = 'Firearms licence (UK, Channel Islands and Isle of Man)',
	ElectoralIDCard = 'Electoral ID card (Northern Ireland only)',
	NonEEADrivingLicense = 'Current driving licence photocard (full or provisional): All countries outside the EEA',
	UKPaperDrivingLicensePre1998 = 'Current driving licence (full or provisional): paper version (if issued before 1998) UK, Isle of Man, Channel Islands and EEA',
	ImmigrationDocumentVisaWorkPermit = 'Immigration document, visa or work permit (Issued by a country outside the EEA: valid only if the applicant is working in the country that issued the document)'
}

export enum GroupTwoADocumentTwo {
	BirthCertificateAfterBirth = 'Birth certificate issued after time of birth (UK, Isle of Man, Channel Islands or Ireland)',
	MarriageCivilPartnershipCertificate = 'Marriage / Civil Partnership certificate (UK, Isle of Man, Channel Islands or Ireland)',
	HMForcesIDCard = 'HM Forces ID card (UK)',
	FirearmsLicence = 'Firearms licence (UK, Channel Islands and Isle of Man)',
	ElectoralIDCard = 'Electoral ID card (Northern Ireland only)',
	NonEEADrivingLicense = 'Current driving licence photocard (full or provisional): All countries outside the EEA',
	UKPaperDrivingLicensePre1998 = 'Current driving licence (full or provisional): paper version (if issued before 1998) UK, Isle of Man, Channel Islands and EEA',
	ImmigrationDocumentVisaWorkPermit = 'Immigration document, visa or work permit (Issued by a country outside the EEA: valid only if the applicant is working in the country that issued the document)'
}

export enum GroupTwoBDocumentOne {
	MortgageStatement = 'Mortgage Statement: UK/EEA (issued within last 12 months)',
	FinancialStatement = 'Financial statement, for example ISA, pension or endowment: UK (issued within last 12 months)',
	P45P60 = 'P45/P60: UK/Channel Islands (issued within last 12 months)',
	LandPropertyRatesDemand = 'Land & Property Services rates demand: NI only (issued within last 12 months)',
	CouncilTaxStatement = 'Council tax statement: GB & Channel Islands (issued within last 12 months)',
	CreditCardBankStatement = 'Credit card/bank or building society statement: UK/EEA (issued within last 3 months)',
	BankOpeningLetter = 'Bank/building society opening letter: UK/EEA (issued within last 3 months)',
	UtilityBill = 'Utility bill: not mobile phone (issued within last 3 months)',
	BenefitStatement = 'Benefit statement i.e. child benefit, pension etc. (issued within last 3 months)',
	GovernmentDocument = 'Central or local government document (issued within last 3 months)',
	EUNationalIDCard = 'EU/EEA National ID card (must be valid)',
	SeniorSmartPass = '60+ or Senior (65+) SmartPass issued by Translink (NI)',
	YLinkCard = 'yLink card issued by Translink (NI)',
	IrishPassportCard = 'Irish Passport Card (Cannot be used with an Irish passport)',
	PASSAccreditationCard = 'Cards carrying the PASS accreditation logo (UK, Isle of Man and Channel Islands)',
	HeadTeacherLetter = 'Letter from head teacher or further education college principal (UK for 16 -19 year olds in full time education: only used in exceptional circumstances if other documents cannot be provided)',
	SponsorshipLetter = 'Letter of sponsorship from future employment provider or voluntary organisation (Non-UK only: valid for applicants residing outside UK at time of application)'
}

export enum GroupTwoBDocumentTwo {
	MortgageStatement = 'Mortgage Statement: UK/EEA (issued within last 12 months)',
	FinancialStatement = 'Financial statement, for example ISA, pension or endowment: UK (issued within last 12 months)',
	P45P60 = 'P45/P60: UK/Channel Islands (issued within last 12 months)',
	LandPropertyRatesDemand = 'Land & Property Services rates demand: NI only (issued within last 12 months)',
	CouncilTaxStatement = 'Council tax statement: GB & Channel Islands (issued within last 12 months)',
	CreditCardBankStatement = 'Credit card/bank or building society statement: UK/EEA (issued within last 3 months)',
	BankOpeningLetter = 'Bank/building society opening letter: UK/EEA (issued within last 3 months)',
	UtilityBill = 'Utility bill: not mobile phone (issued within last 3 months)',
	BenefitStatement = 'Benefit statement i.e. child benefit, pension etc. (issued within last 3 months)',
	GovernmentDocument = 'Central or local government document (issued within last 3 months)',
	EUNationalIDCard = 'EU/EEA National ID card (must be valid)',
	SeniorSmartPass = '60+ or Senior (65+) SmartPass issued by Translink (NI)',
	YLinkCard = 'yLink card issued by Translink (NI)',
	IrishPassportCard = 'Irish Passport Card (Cannot be used with an Irish passport)',
	PASSAccreditationCard = 'Cards carrying the PASS accreditation logo (UK, Isle of Man and Channel Islands)',
	HeadTeacherLetter = 'Letter from head teacher or further education college principal (UK for 16 -19 year olds in full time education: only used in exceptional circumstances if other documents cannot be provided)',
	SponsorshipLetter = 'Letter of sponsorship from future employment provider or voluntary organisation (Non-UK only: valid for applicants residing outside UK at time of application)'
}

export enum SecondaryDocuments {
	BankCard = 'Bank card',
	CreditCard = 'Credit card',
	ForeignBirthCertificate = 'Foreign birth certificate',
	AustralianPrivateHealthInsuranceCard = 'Australian private health insurance card',
	AustralianSecondaryStudentPhotoIdentityDocument = 'Australian secondary student photo identity document',
	AustralianTertiaryStudentPhotoIdentityDocument = 'Australian tertiary student photo identity document',
	AustralianTaxFileNumberDocument = 'Australian tax file number document',
	AustralianTaxationOfficeAssessmentNotice = 'Australian taxation office assessment notice',
	AcademicTranscript = 'Academic transcript from an Australian university',
	BankStatement = 'Bank statement'
	/*
	ConventionTravelDocumentSecondary = 'Convention travel document secondary',
	ForeignDriversLicence = 'Foreign drivers licence',
	OtherForeignGovernmentIssuedDocuments = 'Other foreign government issued documents',
	MedicareCard = 'Medicare card',
	EnrolmentWithAustralianElectoralCommission = 'Enrolment with the Australian Electoral Commission',
	SecurityGuardOrCrowdControlPhotoLicence = 'Security guard/crowd control photo licence',
	EvidenceOfRightToAnAustralianGovenmentBenefit = "Evidence of right to an Australian govenment benefit (Centrelink or Veterans' Affairs)",
	ConsularPhotoIdentityCard = 'Consular photo identity card',
	PhotoIdentityCardIssuedByPoliceForce = 'Photo identity card issued by police force',
	PhotoIdentityCardissuedByTheAdf = 'Photo identity card issued by the Australian Defence Force',
	PhotoIdentityCardIssuedGovernment = 'Photo identity card issued by the Australian Government or a state or territory government',
	AviationSecurityIdentificationCard = 'Aviation Security Identification Card',
	MaritimeSecurityIdentificationCard = 'Maritime Security Identification Card',
	CreditReferenceCheck = 'Credit reference check',
	TrustedRefereesReport = 'Trusted referees report'
	*/
	// The following 2 types are duplicated in CommencementDocuments,
	// delete them for now to quick fix BE validation bug PHOENIX-1752.
	// We should add them back when Workflow.AdditionalDocuments is split into multiple fields. PHOENIX-1755 created.
	// CertificateOfIdentity = 'Certificate of identity',
	// DocumentOfIdentity = 'Document of identity'
}

export enum PrimaryDocumentTypes {
	Passport = 'Passport',
	BirthCertificate = 'Birth Certificate',
	MedicareCard = 'Medicare card',
	DriverLicence = 'Driver Licence'
}

export enum MiscDocumentTypes {
	ReferenceReplacementDocument = 'Reference Replacement Document',
	Attachment = 'Attachment',
	CustomTemplateDocument = 'Custom Template Document'
}

// This name type list should be the same as the list in BE AdditionalName.cs.
export enum AdditionalNameTypes {
	Previous = 'Previous',
	Alias = 'Alias',
	Maiden = 'Maiden'
}

export enum CreditSettingsCheckCreation {
	CreateSingleCheckWithCurrentData = 'createSingleCheckWithCurrentData',
	CreateChecksForAllData = 'createChecksForAllData',
	ScreenerReviewIfMoreThanOneCheckIsRequired = 'screenerReviewIfMoreThanOneCheckIsRequired',
	AlwaysHaveScreenerReview = 'alwaysHaveScreenerReview'
}

export enum CreditSettingsLinkedNames {
	Report = 'report',
	Automatic = 'automatic',
	ReviewByStaff = 'reviewByStaff'
}

export enum NameFieldNames {
	FirstName = 'firstName',
	MiddleNames = 'middleNames',
	LastName = 'lastName',
	GivenNames = 'givenNames',
	FamilyName = 'familyName',
	SingleName = 'singleName',
	MotherMaidenName = 'motherMaidenName'
}

export enum NameTitles {
	Mr = 'Mr',
	Mrs = 'Mrs',
	Ms = 'Ms',
	Miss = 'Miss',
	Other = 'Other'
}

export enum AuStateCode {
	Act = 'ACT',
	Nsw = 'NSW',
	Nt = 'NT',
	Qld = 'QLD',
	Sa = 'SA',
	Tas = 'TAS',
	Vic = 'VIC',
	Wa = 'WA'
}

export enum ReferenceTemplateTypes {
	Employment = 'EREF',
	Education = 'EDREF',
	SelfEmployed = 'SEREF',
	Agency = 'AREF',
	Personal = 'PREF'
}

export enum EventTypes {
	CandidateAccessedIdVerification = 'Candidate accessed ID verification',
	AccessedInvitePage = 'Accessed invite page',
	RefereeConfirmedInformation = 'Referee confirmed information',
	RefereeQuestionsPageAccessed = 'Referee accessed questions page',
	MarkedResultAsInconclusive = 'Mark result as inconclusive',
	MarkedResultAsFlagged = 'Mark result as flagged',
	MarkedResultAsClear = 'Mark result as clear',
	CandidateStartedWorkflow = 'Candidate started workflow',
	IDVLinkExpired = 'IDV link expired',
	GenerateIDVLink = 'Generate IDV link',
	AccessedDataCapturePage = 'Accessed data capture page',
	AskedConsentAgain = 'Asked consent again',
	CandidateCompletedDataCapturePage = 'Candidate completed data capture page',
	AskedRefereeToReconsider = 'Asked referee to reconsider',
	CancelledReference = 'Cancelled reference',
	ResetReferenceResult = 'Reset reference result',
	ScreenerRejectedRefereeContactInformation = 'Screener rejected referee contact information',
	ScreenerApprovedRefereeContactInformation = 'Screener approved referee contact information',
	ConnectIdVerificationCompleted = 'ConnectId verification completed',
	ConnectIdVerificationAborted = 'User aborted connectId verification'
}

export enum FraudTypes {
	SuspectEmail = 'Suspect Email',
	SamePublicIPAddress = 'Same Public IP Address',
	SameBrowserFingerprint = 'Same Browser Fingerprint'
}

export enum RefereeAnswersMarkResults {
	ResultMarkedAsClear = 'Clear',
	ResultMarkedAsInconclusive = 'Inconclusive',
	ResultMarkedAsFlagged = 'Flagged'
}

export enum FormSpecificationSectionIds {
	PrimaryName = 'primaryName',
	AdditionalNamesSection = 'additionalNamesSection',
	AddressHistorySection = 'addressHistorySection'
}

export enum ScreenerActions {
	Save = 'Save',
	SaveAsReviewResult = 'Save as Review result',
	SaveAsClearResult = 'Save as Clear result'
}

export enum RenewalTimePeriod {
	Days = 'Day(s)',
	Weeks = 'Week(s)',
	Months = 'Month(s)',
	Years = 'Year(s)'
}

export enum LoginStep {
	Login = 'Login',
	QRCode = 'QRCode',
	OTP = 'OTP'
}
